import type { AiArticleVariantDto } from './wikiApi';

/**
 * Опрос фоновой задачи ИИ вики (POST → jobId, затем GET раз в пару секунд).
 * Вынесено из модалок «ИИ: из текста» и «ИИ: править статью», чтобы у них была одна
 * логика: ход работы, автоперезапуск при потере задачи, страховка по времени.
 */

/** Ответ опроса `GET /wiki/ai/edit/:jobId`. */
export type AiJobPoll<T> =
  | { status: 'running'; startedAt: string; progress?: unknown }
  | { status: 'done'; startedAt: string; result: T }
  | { status: 'error'; startedAt: string; error: string; code?: string };

export type AiJobOutcome<T> =
  | { kind: 'done'; result: T }
  | { kind: 'error'; error: string }
  /** Окно закрыли или запустили другой запрос — молча выходим. */
  | { kind: 'cancelled' };

export interface RunAiJobOptions<T> {
  /** Запуск задачи на сервере (при автоперезапуске вызывается ещё раз). */
  start: () => Promise<{ jobId: string }>;
  status: (jobId: string) => Promise<AiJobPoll<T>>;
  /** false — окно закрыли/запустили новый запрос: опрос бросаем. */
  alive: () => boolean;
  pollMs: number;
  /** Страховка: сервер не ответил ни done, ни error за это время (на одну попытку). */
  giveUpMs: number;
  giveUpText: string;
  /** После каждого опроса: сколько секунд прошло с начала и что за ход работы прислал сервер. */
  onTick?: (tick: { elapsedSec: number; progress: unknown }) => void;
  /** Сервер потерял задачу (перезапуск api) — запускаем заново, один раз. */
  onRestart?: () => void;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
}

/**
 * Запускает задачу и ждёт её конца. Потеря задачи (`code: 'lost'` — сервер перезапускался)
 * — ОДИН автоматический перезапуск; вторая потеря показывается ошибкой. Обычную ошибку
 * задачи сервер уже сформулировал — отдаём как есть. Сетевые сбои start/status идут
 * исключением (вызывающий покажет `friendlyError`).
 */
export async function runAiJob<T>(opts: RunAiJobOptions<T>): Promise<AiJobOutcome<T>> {
  const sleep = opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const now = opts.now ?? Date.now;
  const t0 = now();

  /** Ждёт одну задачу. 'lost' — сервер потерял её (перезапустился). */
  const wait = async (jobId: string): Promise<AiJobOutcome<T> | { kind: 'lost' }> => {
    const attemptStart = now();
    for (;;) {
      await sleep(opts.pollMs);
      if (!opts.alive()) return { kind: 'cancelled' };
      const st = await opts.status(jobId);
      if (!opts.alive()) return { kind: 'cancelled' };
      if (st.status === 'done') return { kind: 'done', result: st.result };
      if (st.status === 'error') return st.code === 'lost' ? { kind: 'lost' } : { kind: 'error', error: st.error };
      opts.onTick?.({ elapsedSec: Math.round((now() - t0) / 1000), progress: st.progress });
      if (now() - attemptStart > opts.giveUpMs) return { kind: 'error', error: opts.giveUpText };
    }
  };

  let restarted = false;
  for (;;) {
    const { jobId } = await opts.start();
    if (!opts.alive()) return { kind: 'cancelled' };
    const res = await wait(jobId);
    if (res.kind !== 'lost') return res;
    // Сервер сказал «запускаем заново», но второй раз мы не перезапускаем — честно говорим, что делать.
    if (restarted) return { kind: 'error', error: 'Сервер снова перезапускался во время работы ИИ — попробуйте ещё раз через минуту' };
    restarted = true;
    opts.onRestart?.();
  }
}

// ─── «ИИ: из текста» — ход работы ──────────────────────────────────

/** Ход «ИИ: из текста» (`progress` задачи): сколько запросов закончено, сколько знаков написано, готовые варианты. */
export interface VariantsProgress {
  done: number;
  total: number;
  chars: number;
  variants: AiArticleVariantDto[];
}

const isVariant = (v: unknown): v is AiArticleVariantDto =>
  !!v && typeof v === 'object' && typeof (v as { title?: unknown }).title === 'string' && typeof (v as { bodyMd?: unknown }).bodyMd === 'string';

/** Ход работы приходит как `unknown` (бэкенд мог быть старой версии) — берём только то, что понимаем. */
export function readVariantsProgress(p: unknown): VariantsProgress | null {
  if (!p || typeof p !== 'object') return null;
  const o = p as Record<string, unknown>;
  const num = (x: unknown): number => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? x : 0);
  const total = Math.floor(num(o.total));
  if (total < 1) return null;
  return {
    done: Math.min(total, Math.floor(num(o.done))),
    total,
    chars: Math.floor(num(o.chars)),
    variants: Array.isArray(o.variants) ? o.variants.filter(isVariant) : [],
  };
}

/** «5 знаков» / «1 знак» / «2 знака». */
export function pluralRu(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** 45 → «45 с», 125 → «2 мин 05 с». */
export function formatElapsed(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s} с`;
  return `${Math.floor(s / 60)} мин ${String(s % 60).padStart(2, '0')} с`;
}

/** «ИИ пишет варианты… готово 1 из 2 · написано 6 300 знаков · 45 с». */
export function variantsStatusText(p: VariantsProgress | null, elapsedSec: number): string {
  const parts: string[] = [];
  if (p) {
    parts.push(`готово ${p.done} из ${p.total}`);
    if (p.chars > 0) parts.push(`написано ${p.chars.toLocaleString('ru-RU')} ${pluralRu(p.chars, 'знак', 'знака', 'знаков')}`);
  }
  parts.push(formatElapsed(elapsedSec));
  return `ИИ пишет варианты… ${parts.join(' · ')}`;
}

/**
 * Ключ варианта для React и «Показать текст»: индекс нельзя — варианты приходят по мере
 * готовности и готовый вариант с меньшим номером встанет перед уже показанными.
 */
export function variantKey(v: AiArticleVariantDto): string {
  return `${v.approach}|${v.title}|${v.bodyMd.length}`;
}

/** Ключи всего списка; одинаковые варианты (редко: GigaChat) различаем суффиксом, чтобы ключи React не совпали. */
export function variantKeys(list: AiArticleVariantDto[]): string[] {
  const seen = new Map<string, number>();
  return list.map((v) => {
    const k = variantKey(v);
    const n = seen.get(k) ?? 0;
    seen.set(k, n + 1);
    return n ? `${k}#${n}` : k;
  });
}
